import React, { useState } from "react";
import { Dropdown, DropdownToggleProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as ArrowRightSVGMuted } from "../../../../../images/svg/dashboard/arrow-right-muted.svg";
import { ReactComponent as ArrowRightSVG } from "../../../../../images/svg/dashboard/arrow-right.svg";
import useDashboardContext from "../../../services/hooks/useDashboardContext";
import { ChartDataRangeType, ChartType, DashboardCardConfig, chartDataRangeOptions } from "../../../services/types";
import { getSeeMoreLink } from "../../../utils/utils";

interface Props {
	config: DashboardCardConfig;
	updateChartRangeType: (rangeType: ChartDataRangeType) => void;
}

type ChartDropdownOption = {
	type: ChartDataRangeType;
	name: string;
};

const ReportItemWrapperFooter = ({
	updateChartRangeType,
	config: { chartType, chartDataRangeType, reportNameToRedirectTo },
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const {
		state: {
			filtersConfig: {
				selectedFilter: { dateRange },
			},
		},
		isEditMode,
	} = useDashboardContext();
	const dropdownOptions = chartDataRangeOptions.map((type) => {
		return {
			name: t(`go_report.chart.aggregates.${type}`, { ns: "lib" }),
			type,
		} as ChartDropdownOption;
	});
	const [dropdownIsActive, setDropdownIsActive] = useState<boolean>(false);

	return (
		<div className="d-flex mt-3 justify-content-between">
			{chartType !== ChartType.BOX &&
				chartType !== ChartType.PROGRESS_BAR &&
				chartType !== ChartType.HORIZONTAL_BAR && (
					<Dropdown onToggle={() => setDropdownIsActive((prevState) => !prevState)}>
						<Dropdown.Toggle disabled={isEditMode} as={CustomToggle}>
							{t(`go_report.chart.aggregates.${chartDataRangeType}`, { ns: "lib" })}
							<DropdownSVG
								className={`${dropdownIsActive ? "triangle-rotate-active" : ""} dropdown-svg`}
							/>
						</Dropdown.Toggle>
						<Dropdown.Menu>
							{dropdownOptions.map((item) => {
								return (
									<Dropdown.Item
										active={chartDataRangeType === item.type}
										key={item.type}
										onClick={() => updateChartRangeType(item.type)}
									>
										{item.name}
									</Dropdown.Item>
								);
							})}
						</Dropdown.Menu>
					</Dropdown>
				)}

			{isEditMode ? (
				<div className="d-flex align-items-center">
					<span className="font-weight-bold text-muted">{t("go_report.action.see_more.title")}</span>
					<ArrowRightSVGMuted className="ms-1" />
				</div>
			) : (
				<Link
					to={getSeeMoreLink({
						dateRange,
						reportName: reportNameToRedirectTo,
						organizationId: organization.id,
						withCompareParam: true,
					})}
					className={"font-weight-bold"}
				>
					{t("go_report.action.see_more.title")}
					<ArrowRightSVG className="ms-1" />
				</Link>
			)}
		</div>
	);
};
export default ReportItemWrapperFooter;

const CustomToggle = React.forwardRef<HTMLElement, DropdownToggleProps>(({ children, onClick, disabled }, ref) => {
	return (
		<span
			style={{ cursor: "pointer" }}
			className={disabled ? "text-muted" : ""}
			onClick={disabled ? undefined : onClick}
			ref={ref}
		>
			{children}
		</span>
	);
});
CustomToggle.displayName = "CustomToggle";
